import React, { useState } from 'react';
import { Box, TextField } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import {
  ContentCardContainer,
  ContentCardHeader
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/UtilizationCardContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import { EditIcon2 } from 'src/components/SvgIcons';
import { processContent, createOptimisticContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import { useKeywordsDisplayStore, useEditingFieldsStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { KeywordContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { useEditGeneratedContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useEditGeneratedContent';
import { getQueryParamValue } from 'src/utils/app';

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    borderRadius: '4px',
    border: `1px solid ${colors.grey}`,
    backgroundColor: 'white',
    fontSize: '14px',
    padding: '10px 14px',
    boxSizing: 'border-box',
    minHeight: '48px',
    '&:hover': {
      borderColor: colors.darkBlue
    },
    '&.Mui-focused': {
      borderColor: colors.darkBlue
    },
    '& .MuiInputBase-input': {
      padding: '0px'
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
});

type EditableContentCardProps = {
  title: string;
  content: KeywordContent[];
  side: 'current' | 'generated';
  fieldName: string; // e.g., 'currentContent.title' or 'generatedContent.title'
  queryKey: readonly unknown[]; // Query key for the data being edited
  flexHeight?: string;
  boxHeight?: string;
  placeholderText?: string;
  isEditable?: boolean;
};

export const EditableContentCard = ({
  title,
  content,
  side,
  fieldName,
  queryKey,
  placeholderText = 'No content available',
  isEditable = true
}: EditableContentCardProps) => {
  const { showKeywords } = useKeywordsDisplayStore();
  const { setFieldEditing } = useEditingFieldsStore();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [originalValue, setOriginalValue] = useState<string>('');
  const methods = useFormContext();
  const { control } = methods;
  const { editGeneratedContent, isPending } = useEditGeneratedContent();
  const contentGenId = getQueryParamValue('contentGenId', '');
  const formValue = methods.watch(fieldName);
  const queryClient = useQueryClient();

  const handleEditClick = () => {
    if (isEditable) {
      const currentValue = methods.getValues(fieldName);
      setOriginalValue(currentValue);
      setIsEditMode(true);
      setFieldEditing(fieldName, true);
    }
  };

  const handleSave = async () => {
    const editedValue = formValue || methods.getValues(fieldName);
    
    // Commit the edited value to the form
    methods.setValue(fieldName, editedValue, { shouldDirty: true, shouldValidate: false, shouldTouch: true });
    await methods.trigger(fieldName);
    
    // Optimistically update the query cache
    if (content && content.length > 0) {
      const newOptimisticContent = createOptimisticContent(content, editedValue);
      
      // Get section name from fieldName (e.g., 'generatedContent.title' -> 'Title')
      let sectionName = '';
      if (fieldName.includes('.title')) {
        sectionName = 'Title';
      } else if (fieldName.includes('.description')) {
        sectionName = 'Description';
      } else if (fieldName.includes('.bullets.')) {
        // Extract bullet index for proper section name (e.g., 'Bullet 1', 'Bullet 2')
        const bulletMatch = fieldName.match(/\.bullets\.(\d+)/);
        if (bulletMatch) {
          const bulletIndex = parseInt(bulletMatch[1], 10);
          sectionName = `Bullet ${bulletIndex + 1}`; // API uses 1-based indexing
        }
      }
      
      // Determine which side to update
      const targetSide = side === 'generated' ? 'right' : 'left';
      
      // Update query cache
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData) return oldData;
        
        const updatedSections = oldData[targetSide]?.map((section: any) => {
          if (section.name === sectionName) {
            return {
              ...section,
              content: newOptimisticContent
            };
          }
          return section;
        });
        
        return {
          ...oldData,
          [targetSide]: updatedSections
        };
      });
    }
    
    setIsEditMode(false);
    setFieldEditing(fieldName, false);

    // Get all form values
    const formValues = methods.getValues();
    const generatedContent = formValues.generatedContent;

    // For bullet fields, manually update the specific index and override the entire bullets array
    let finalBullets = generatedContent.bullets || [];
    if (fieldName.startsWith('generatedContent.bullets.')) {
      const bulletMatch = fieldName.match(/generatedContent\.bullets\.(\d+)/);
      if (bulletMatch) {
        const bulletIndex = parseInt(bulletMatch[1], 10);
        finalBullets = [...(generatedContent.bullets || [])];
        finalBullets[bulletIndex] = editedValue;
        // Explicitly update the entire bullets array in the form
        methods.setValue('generatedContent.bullets', finalBullets, { shouldDirty: true });
      }
    }

    // Call API to save changes
    if (contentGenId && side === 'generated') {
      editGeneratedContent(
        {
          skuContentGenerationId: contentGenId,
          content: {
            title: generatedContent.title || '',
            featureBullets: finalBullets,
            description: generatedContent.description || ''
          }
        },
        {
          onSuccess: () => {
            // Content will be refreshed via query invalidation
          },
          onError: () => {
            // Revert the query cache on error
            queryClient.invalidateQueries({ queryKey });
            methods.setValue(fieldName, originalValue);
          }
        }
      );
    }
  };

  const handleCancel = () => {
    methods.setValue(fieldName, originalValue);
    setIsEditMode(false);
    setFieldEditing(fieldName, false);
  };

  return (
    <Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} sx={{ width: '50%' }}>
      <ContentCardContainer sx={{ padding: '0px' }}>
        <ContentCardHeader title={title}>
          {isEditMode ? (
              <Flex gap={8} alignItems="center">
                <SlButton
                  variant="contained"
                  onClick={handleSave}
                  disabled={isPending}
                  buttonSx={{
                    minHeight: '32px',
                    padding: '4px 12px'
                  }}
                >
                  {isPending ? 'Saving...' : 'Save'}
                </SlButton>

                <SlButton
                  variant="normal"
                  onClick={handleCancel}
                  disabled={isPending}
                  buttonSx={{
                    minHeight: '32px',
                    padding: '4px 12px',
                    border: `1px solid ${colors.graysSecondary}`
                  }}
                >
                  Cancel
                </SlButton>
              </Flex>
          ) : (
            isEditable &&
            isHovered && (
              <EditIcon2
                onClick={handleEditClick}
                style={{
                  width: 16,
                  height: 16,
                  marginRight: 'auto',
                  marginLeft: '8px',
                  cursor: 'pointer'
                }}
              />
            )
          )}
        </ContentCardHeader>

        {isEditMode ? (
          <Box
            sx={{
              padding: '8px'
            }}
          >
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  multiline
                  rows={title === 'Description' ? 12 : 3}
                  placeholder={placeholderText}
                  fullWidth
                  autoFocus
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: title === 'Description' ? '300px' : '80px'
                    },
                    // WebKit scrollbar styling
                    '& .MuiInputBase-inputMultiline::-webkit-scrollbar, & textarea::-webkit-scrollbar': {
                      width: '5px'
                    },
                    '& .MuiInputBase-inputMultiline::-webkit-scrollbar-thumb, & textarea::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: '4px'
                    }
                  }}
                />
              )}
            />
          </Box>
        ) : (
          <Box
            sx={{
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              padding: '0px',
              borderRadius: '8px',
              height: 'fit-content',
              overflow: 'auto',
              gap: '4px',
              lineHeight: '20px',
              '& > span': {
                display: 'inline-block',
                marginRight: '4px',
                marginBottom: '4px'
              }
            }}
          >
            {(() => {
              // Show keyword-highlighted content from API/cache (includes optimistic updates)
              if (content && content.length > 0) {
                return processContent({ phrases: content }, showKeywords);
              }
              // If no API content but we have form value
              if (formValue && typeof formValue === 'string' && formValue.trim()) {
                return <Text>{formValue}</Text>;
              }
              // Finally, show placeholder
              return <Text sx={{ color: colors.errorRed }}>{placeholderText}</Text>;
            })()}
          </Box>
        )}
      </ContentCardContainer>
    </Box>
  );
};
